/** Phase 9 Slice 7 — /app/quality/rubrics — Tenant rubric editor.
 *
 *  - Lists all rubrics with status badges + latest version metadata.
 *  - "Neue Rubrik" creates a v1 draft.
 *  - Click a card → editor drawer with dimensions repeater, weight-sum
 *    indicator (must hit 1.0 ± 0.01 to enable Publish), live JSON-diff
 *    history pane against the previous version.
 *  - Publish button disabled until weight-sum OK AND there is ≥1 dimension.
 *  - Producer/analyst see disabled CTAs (RBAC mirrored client-side; the
 *    server is the source of truth).
 */
import { useEffect, useMemo, useState } from "react";
import { api, auth } from "../../lib/api";
import { useLocale } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle }
  from "../../components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Lock, Plus, Trash2 } from "lucide-react";

const COPY = {
  de: {
    title: "Quality Rubriken",
    subtitle: "Tenant-eigene, versionierte Rubriken für den LLM-Judge.",
    new: "Neue Rubrik", create: "Erstellen", cancel: "Abbrechen",
    name: "Name", slug: "Slug (unveränderlich)", desc: "Beschreibung",
    version: "Version", status: "Status", weight_sum: "Gewichtssumme",
    publish: "Veröffentlichen", add_dim: "Dimension hinzufügen",
    archive: "Archivieren", clone_draft: "Neuer Entwurf",
    history: "Historie",
    forbidden: "Keine Berechtigung.",
  },
  en: {
    title: "Quality Rubrics",
    subtitle: "Tenant-owned, versioned rubrics for the LLM judge.",
    new: "New rubric", create: "Create", cancel: "Cancel",
    name: "Name", slug: "Slug (immutable)", desc: "Description",
    version: "Version", status: "Status", weight_sum: "Weight sum",
    publish: "Publish", add_dim: "Add dimension",
    archive: "Archive", clone_draft: "New draft",
    history: "History",
    forbidden: "Not authorised.",
  },
};
const tt = (l, k) => (COPY[l] || COPY.de)[k] || k;

function statusBadge(s) {
  const v = { draft: "secondary", published: "default",
                  archived: "outline" }[s] || "outline";
  return <Badge variant={v} className="font-mono text-[10px]">{s}</Badge>;
}

export default function QualityRubrics() {
  const { locale } = useLocale();
  const user = auth.getUser() || { role: "" };
  const canWrite = ["owner", "admin", "manager"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);  // rubric_id

  async function load() {
    try {
      const r = await api.get("/quality/rubrics");
      setRows(r.data.rubrics || []);
    } catch (_) {}
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 space-y-6" data-testid="quality-rubrics-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {tt(locale, "title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {tt(locale, "subtitle")}
          </p>
        </div>
        <Button data-testid="rubric-new-btn"
                disabled={!canWrite}
                onClick={() => setCreating(true)}>
          <Plus className="size-4 mr-1" /> {tt(locale, "new")}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((r) => (
          <Card key={r.id}
                data-testid={`rubric-card-${r.slug}`}
                className="cursor-pointer hover:shadow-md"
                onClick={() => setEditing(r.id)}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-sm">{r.name}</CardTitle>
                {r.source === "system" && (
                  <Badge variant="outline" className="font-mono text-[10px]">
                    <Lock className="size-3 mr-1" /> system
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-1 text-xs">
              <p className="text-muted-foreground line-clamp-2">
                {r.description || "—"}
              </p>
              <div className="flex flex-wrap gap-1 pt-1">
                <Badge variant="outline" className="font-mono text-[10px]">
                  slug: {r.slug}
                </Badge>
                {r.latest_published && (
                  <Badge variant="default" className="font-mono text-[10px]">
                    v{r.latest_published.version_number} published
                  </Badge>
                )}
                {r.latest_draft && (
                  <Badge variant="secondary" className="font-mono text-[10px]">
                    v{r.latest_draft.version_number} draft
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {creating && (
        <CreateRubricDialog locale={locale}
                                  onClose={() => setCreating(false)}
                                  onCreated={async (id) => {
                                    setCreating(false);
                                    await load();
                                    setEditing(id);
                                  }}/>
      )}

      {editing && (
        <RubricEditorDialog rubricId={editing} locale={locale}
                                  canWrite={canWrite}
                                  onClose={async () => {
                                    setEditing(null);
                                    await load();
                                  }}/>
      )}
    </div>
  );
}

function CreateRubricDialog({ locale, onClose, onCreated }) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  async function submit() {
    try {
      const r = await api.post("/quality/rubrics",
                                 { slug, name, description: desc });
      toast.success("✓");
      onCreated(r.data.rubric.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "error");
    }
  }
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="rubric-create-dialog">
        <DialogHeader>
          <DialogTitle>{tt(locale, "new")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">{tt(locale, "slug")}</Label>
            <Input data-testid="rubric-slug-input"
                    value={slug} onChange={(e) => setSlug(e.target.value)}/>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{tt(locale, "name")}</Label>
            <Input data-testid="rubric-name-input"
                    value={name} onChange={(e) => setName(e.target.value)}/>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{tt(locale, "desc")}</Label>
            <Input value={desc} onChange={(e) => setDesc(e.target.value)}/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {tt(locale, "cancel")}
          </Button>
          <Button data-testid="rubric-create-submit"
                  disabled={!slug || !name} onClick={submit}>
            {tt(locale, "create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RubricEditorDialog({ rubricId, locale, canWrite, onClose }) {
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(null);    // current draft RubricVersion
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const r = await api.get(`/quality/rubrics/${rubricId}`);
      setDetail(r.data);
      const d = (r.data.versions || []).find((v) => v.status === "draft");
      setDraft(d || null);
    } catch (_) {}
  }
  useEffect(() => { load(); }, [rubricId]);

  async function createDraft() {
    try {
      const r = await api.post(`/quality/rubrics/${rubricId}/versions`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "error");
    }
  }

  function setDim(idx, k, v) {
    setDraft((d) => {
      const dims = [...(d.dimensions || [])];
      dims[idx] = { ...dims[idx], [k]: v };
      return { ...d, dimensions: dims };
    });
  }
  function addDim() {
    setDraft((d) => ({
      ...d,
      dimensions: [
        ...(d.dimensions || []),
        { label: "new_dim", weight: 0, description: "",
          guidance: "", min_score: 0, max_score: 10, examples: {} },
      ],
    }));
  }
  function rmDim(idx) {
    setDraft((d) => ({
      ...d,
      dimensions: (d.dimensions || []).filter((_, i) => i !== idx),
    }));
  }

  const weightSum = useMemo(() => {
    if (!draft) return 0;
    return (draft.dimensions || []).reduce(
      (a, x) => a + (Number(x.weight) || 0), 0);
  }, [draft]);
  const weightOk = Math.abs(weightSum - 1) <= 0.01;

  async function saveDraft() {
    if (!draft) return;
    setSaving(true);
    try {
      await api.patch(
        `/quality/rubrics/${rubricId}/versions/${draft.version_number}`,
        { dimensions: draft.dimensions,
          scoring_scale: draft.scoring_scale,
          pass_threshold: draft.pass_threshold,
          change_note: draft.change_note });
      toast.success("✓");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "error");
    } finally { setSaving(false); }
  }

  async function publish() {
    if (!draft) return;
    try {
      await api.post(
        `/quality/rubrics/${rubricId}/versions/${draft.version_number}/publish`);
      toast.success("✓");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "error");
    }
  }

  if (!detail) return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>Loading…</DialogContent>
    </Dialog>
  );
  const r = detail.rubric;

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="rubric-editor"
        className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{r.name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Editor column */}
          <div className="md:col-span-2 space-y-3">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">slug:</span>
              <code className="font-mono">{r.slug}</code>
              {r.source === "system" && (
                <Badge variant="outline" className="text-[10px]">
                  <Lock className="size-3 mr-1" />system
                </Badge>
              )}
            </div>

            {!draft ? (
              <div className="rounded-md border border-dashed p-4 text-sm
                                text-muted-foreground">
                <p>No open draft.</p>
                <Button size="sm" className="mt-2"
                        disabled={!canWrite}
                        data-testid="rubric-clone-draft"
                        onClick={createDraft}>
                  {tt(locale, "clone_draft")}
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-2"
                      data-testid="rubric-dims-repeater">
                  {(draft.dimensions || []).map((d, i) => (
                    <div key={i}
                          data-testid={`rubric-dim-${i}`}
                          className="grid grid-cols-12 gap-2 items-end
                                      border rounded-md p-2">
                      <div className="col-span-3 space-y-1">
                        <Label className="text-[10px]">label</Label>
                        <Input value={d.label || ""}
                                onChange={(e) => setDim(i, "label", e.target.value)}/>
                      </div>
                      <div className="col-span-2 space-y-1">
                        <Label className="text-[10px]">weight</Label>
                        <Input type="number" step="0.05" min={0} max={1}
                                data-testid={`rubric-dim-weight-${i}`}
                                value={d.weight ?? 0}
                                onChange={(e) => setDim(i, "weight",
                                                              Number(e.target.value))}/>
                      </div>
                      <div className="col-span-6 space-y-1">
                        <Label className="text-[10px]">guidance</Label>
                        <Input value={d.guidance || ""}
                                onChange={(e) => setDim(i, "guidance", e.target.value)}/>
                      </div>
                      <Button variant="ghost" size="sm"
                              className="col-span-1"
                              onClick={() => rmDim(i)}>
                        <Trash2 className="size-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm"
                        data-testid="rubric-add-dim"
                        onClick={addDim}>
                  <Plus className="size-3 mr-1" />
                  {tt(locale, "add_dim")}
                </Button>
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-3">
            <div className="rounded-md border p-3 text-xs space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {tt(locale, "weight_sum")}
                </span>
                <span data-testid="rubric-weight-sum"
                        className={weightOk
                          ? "font-mono text-emerald-700 dark:text-emerald-300"
                          : "font-mono text-rose-700 dark:text-rose-300"}>
                  {weightSum.toFixed(2)} / 1.00
                </span>
              </div>
            </div>

            <Button className="w-full" size="sm"
                    disabled={!canWrite || !draft || saving}
                    onClick={saveDraft}>
              {saving ? "…" : "Save draft"}
            </Button>
            <Button className="w-full" size="sm"
                    data-testid="rubric-publish-btn"
                    disabled={!canWrite || !draft || !weightOk
                                  || (draft?.dimensions || []).length === 0}
                    onClick={publish}>
              {tt(locale, "publish")}
            </Button>

            <div className="rounded-md border p-2 text-[11px]">
              <div className="font-medium mb-1">{tt(locale, "history")}</div>
              <ul className="space-y-1" data-testid="rubric-history">
                {(detail.versions || []).map((v) => (
                  <li key={v.id} className="flex items-center justify-between"
                      data-testid={`rubric-history-v${v.version_number}`}>
                    <span>v{v.version_number}</span>
                    {statusBadge(v.status)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
